import { useTranslation } from "react-i18next";
import { Suspense, useEffect } from "react";
import { Outlet, useLocation } from "react-router";
import { PaymentFailedBanner } from "@/components/billing/payment-failed-banner";
import { MobileNav } from "./mobile-nav";
import { OfflineBanner, PwaUpdate } from "@/components/pwa/pwa";
import { SiteHeader } from "./site-header";
import { UpgradeDialog } from "@/components/upgrade/upgrade-dialog";
import { useCompany } from "@/hooks/use-company";
import { setUsSpelling } from "@/i18n/us-spelling";
import { useAuth } from "@/lib/auth/auth-context";
import { env } from "@/lib/core/env";
import { cn } from "@/lib/core/utils";

export function Layout() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  // A business registered in the US reads US spelling (i18n/us-spelling.ts).
  const country = useCompany({ enabled: isAuthenticated }).data?.country;
  useEffect(() => setUsSpelling(isAuthenticated && country === "US"), [isAuthenticated, country]);
  // The assistant is a full-screen app below the header (no padding, no footer, own scrolling).
  const fullScreen = useLocation().pathname.startsWith("/assistant");
  if (fullScreen) {
    return (
      <div className="flex h-dvh flex-col">
        <SiteHeader />
        {isAuthenticated && <PaymentFailedBanner />}
        <OfflineBanner />
        <PwaUpdate />
        <main className="min-h-0 flex-1">
          <Suspense fallback={<PageLoading />}>
            <Outlet />
          </Suspense>
        </main>
        <UpgradeDialog />
      </div>
    );
  }
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      {isAuthenticated && <PaymentFailedBanner />}
      <OfflineBanner />
      <PwaUpdate />
      {/* Signed in on a phone, the tab bar covers the bottom: leave room for it. */}
      <main
        className={cn(
          "container mx-auto w-full max-w-7xl flex-1 px-4 py-6 md:py-8",
          isAuthenticated && "pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-8",
        )}
      >
        <Suspense fallback={<PageLoading />}>
          <Outlet />
        </Suspense>
      </main>
      <footer
        className={cn(
          "border-t py-6 text-center text-xs text-muted-foreground",
          isAuthenticated && "hidden md:block",
        )}
      >
        {env.appName} · {t("app.tagline")} · v{env.appVersion}
      </footer>
      {isAuthenticated && <MobileNav />}
      <UpgradeDialog />
    </div>
  );
}

/** While a page's code downloads (usually a few ms from the service worker cache). */
function PageLoading() {
  return <div className="min-h-[50vh]" aria-busy="true" data-testid="page-loading" />;
}
